import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Guide {
  id: string;
  title: string;
  summary: string;
  body: string | null;
  tag: string;
  read_time: string;
  author: string;
  image: string;
  created_at: string;
}

interface GuideForm {
  title: string;
  summary: string;
  body: string;
  tag: string;
  read_time: string;
  author: string;
  image: string;
}

const emptyForm: GuideForm = { title: '', summary: '', body: '', tag: '', read_time: '', author: '', image: '' };

export default function GuidesManager() {
  const [items, setItems] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<GuideForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase.from('buying_guides').select('*').order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setItems(data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (g: Guide) => {
    setEditingId(g.id);
    setForm({ title: g.title, summary: g.summary, body: g.body || '', tag: g.tag, read_time: g.read_time, author: g.author, image: g.image });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this guide?')) return;
    try {
      await supabase.from('buying_guides').delete().eq('id', id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await supabase.from('buying_guides').update(form).eq('id', editingId);
      } else {
        await supabase.from('buying_guides').insert(form);
      }
      setShowForm(false);
      fetchItems();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-ink-500">Loading...</div>;
  if (error) return <div className="py-20 text-center"><p className="text-rose-600 text-sm mb-3">{error}</p><button onClick={fetchItems} className="h-9 px-4 rounded-lg bg-ink-900 text-white text-sm font-semibold whitespace-nowrap">Retry</button></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold text-ink-900">Buying Guides ({items.length})</h2>
        <button onClick={openAdd} className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold whitespace-nowrap flex items-center gap-1.5 transition">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
          Add Guide
        </button>
      </div>

      <div className="bg-white rounded-xl border border-ink-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-ink-50/70 border-b border-ink-100">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Guide</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Tag</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Author</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Read Time</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-ink-500 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(g => (
                <tr key={g.id} className="border-b border-ink-100 last:border-b-0 hover:bg-ink-50/50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {g.image && (
                        <div className="w-16 h-10 rounded-lg bg-ink-100 overflow-hidden flex-shrink-0">
                          <img src={g.image} alt={g.title} className="w-full h-full object-cover object-top" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-ink-900 truncate max-w-[280px]">{g.title}</p>
                        <p className="text-xs text-ink-500 truncate max-w-[280px]">{g.summary}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="inline-flex h-5 px-1.5 rounded bg-ink-50 text-ink-600 text-[10px] font-bold uppercase whitespace-nowrap">{g.tag}</span></td>
                  <td className="px-4 py-3 text-sm text-ink-700">{g.author}</td>
                  <td className="px-4 py-3 text-sm text-ink-500">{g.read_time}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(g)} className="w-8 h-8 rounded-lg hover:bg-ink-100 text-ink-600 flex items-center justify-center transition"><i className="ri-edit-line text-sm"></i></button>
                      <button onClick={() => handleDelete(g.id)} className="w-8 h-8 rounded-lg hover:bg-rose-50 text-ink-600 hover:text-rose-600 flex items-center justify-center transition"><i className="ri-delete-bin-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
          <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" onClick={() => setShowForm(false)}></div>
          <div className="relative bg-white rounded-2xl border border-ink-100 shadow-card w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-ink-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-ink-900">{editingId ? 'Edit Guide' : 'Add Guide'}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-lg hover:bg-ink-50 flex items-center justify-center text-ink-500 transition"><i className="ri-close-line"></i></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Title</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Summary</label>
                <textarea value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition resize-none" required />
                <p className="mt-1 text-[11px] text-ink-400">Short teaser shown on guide cards.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">Article Content</label>
                <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={8} className="w-full px-3 py-2 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition resize-none" placeholder="Full guide text. Separate paragraphs with a blank line." />
                <p className="mt-1 text-[11px] text-ink-400">Shown on the guide's detail page. Leave blank to fall back to the summary.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Tag</label>
                  <input value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Read Time</label>
                  <input value={form.read_time} onChange={e => setForm({ ...form, read_time: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Author</label>
                  <input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-700 mb-1">Image URL</label>
                  <input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-ink-200 text-sm focus:outline-none focus:border-cta-400 transition" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="h-9 px-4 rounded-lg border border-ink-200 text-sm font-semibold text-ink-600 hover:bg-ink-50 whitespace-nowrap transition">Cancel</button>
                <button type="submit" disabled={saving} className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-sm font-semibold whitespace-nowrap transition disabled:opacity-50">{saving ? 'Saving...' : editingId ? 'Save' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}